import React, { useState, useEffect, useCallback } from 'react';
import { useAutoSave } from '../hooks/useAutoSave';
import { tenantSettingsAPI } from '../services/api';
import InfoIconButton from '../components/InfoIconButton';
import './TenantSettings.css';

const DEFAULT_MISSING_BODY =
  'The following clients are missing a school consent document in their Intake-Consents folder. Please obtain and upload the signed consent for each.\n\n{client_lines}';

const DEFAULT_DELAYED_BODY =
  '{child_link} has {count} service need(s) unmatched {threshold_days}+ days after the Date of Consent. Please record a delay reason/comment on each, or match a provider.\n\n{need_lines}';

function CollapsibleBody({ id, value, onChange, placeholder, rows = 10 }) {
  const [expanded, setExpanded] = useState(false);

  const lines = (value || '').split('\n');
  const previewText = lines.slice(0, 2).join('\n');
  const hasMore = lines.length > 2 || (value || '').length > 120;

  if (!expanded) {
    return (
      <div className="nt-body-collapsed">
        <div className="nt-body-preview">
          {value
            ? <><span>{previewText}</span>{hasMore && <span className="nt-body-more">…</span>}</>
            : <span className="nt-body-empty">{placeholder}</span>}
        </div>
        <button type="button" className="nt-body-toggle" onClick={() => setExpanded(true)}>
          {value ? 'Edit' : 'Add'}
        </button>
      </div>
    );
  }

  return (
    <div className="nt-body-expanded">
      <textarea
        id={id}
        rows={rows}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={onChange}
        className="other-textarea"
        autoFocus
      />
      <button type="button" className="nt-body-toggle nt-body-toggle--collapse" onClick={() => setExpanded(false)}>
        Collapse
      </button>
    </div>
  );
}

const CRON_LABELS = {
  '0 8 * * *':  'Daily at 8 AM',
  '0 8 * * 1':  'Weekly — Mon at 8 AM',
  '0 8 1 * *':  'Monthly — 1st at 8 AM',
  '0 8 15 * *': 'Monthly — 15th at 8 AM',
};

function cronLabel(expr) {
  return CRON_LABELS[expr] ?? expr;
}

function JobScheduleBadge({ schedules, jobCode }) {
  if (!schedules) return null;
  const config = schedules[jobCode];
  if (!config) return <span className="nt-schedule-badge nt-schedule-badge--none">No schedule configured</span>;
  if (!config.enabled) return <span className="nt-schedule-badge nt-schedule-badge--disabled">Disabled · {cronLabel(config.cron_expression)}</span>;
  return <span className="nt-schedule-badge nt-schedule-badge--active">Schedule: {cronLabel(config.cron_expression)}</span>;
}

function toFormValues(settings) {
  return {
    recredential_notice_details:  settings?.recredential_notice_details  ?? '',
    eom_checklist_notice_details: settings?.eom_checklist_notice_details ?? '',
    // Pre-fill with default so the field shows "Edit" and the actual text that will be sent
    school_consent_missing_body:  settings?.school_consent_missing_body  || DEFAULT_MISSING_BODY,
    delayed_need_reminder_body:   settings?.delayed_need_reminder_body   || DEFAULT_DELAYED_BODY,
  };
}

export default function NotificationTemplates() {
  const [formValues, setFormValues] = useState(toFormValues(null));
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [schedules, setSchedules] = useState(null);
  const [headerHintOpen, setHeaderHintOpen] = useState(false);
  const [recredHintOpen, setRecredHintOpen] = useState(false);
  const [eomHintOpen, setEomHintOpen] = useState(false);
  const [schoolHintOpen, setSchoolHintOpen] = useState(false);
  const [schoolVarsHintOpen, setSchoolVarsHintOpen] = useState(false);
  const [delayedHintOpen, setDelayedHintOpen] = useState(false);
  const [delayedVarsHintOpen, setDelayedVarsHintOpen] = useState(false);

  useEffect(() => {
    tenantSettingsAPI.get()
      .then((res) => setFormValues(toFormValues((res.data.data || {}).settings)))
      .catch(() => {});
    tenantSettingsAPI.getJobSchedules()
      .then((res) => setSchedules(res.data?.data || {}))
      .catch(() => setSchedules({}));
  }, []);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setSaveTrigger((n) => n + 1);
  };

  const performSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      const trim = (v) => (v === '' || v == null ? null : String(v).trim() || null);
      await tenantSettingsAPI.update({
        recredential_notice_details:  trim(formValues.recredential_notice_details),
        eom_checklist_notice_details: trim(formValues.eom_checklist_notice_details),
        school_consent_missing_body:  trim(formValues.school_consent_missing_body),
        delayed_need_reminder_body:   trim(formValues.delayed_need_reminder_body),
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch {
      setSaveStatus('error');
    }
  }, [formValues]);

  useAutoSave(saveTrigger, performSave, { enabled: true, delayMs: 500 });

  return (
    <div className="tenant-settings-page">
      <div className="tenant-settings-header">
        <div className="tenant-settings-header-left">
          <h1 className="nt-page-title">
            Scheduled job templates
            <InfoIconButton
              ariaExpanded={headerHintOpen}
              ariaControls="nt-header-hint"
              onClick={() => setHeaderHintOpen((v) => !v)}
              ariaLabel="Show link placeholder info"
              ariaLabelExpanded="Hide link placeholder info"
            />
          </h1>
          {headerHintOpen && (
            <p id="nt-header-hint" className="nt-hint-text">
              Use <code>{'{tenant_code}'}</code> in links as a placeholder — replaced with your
              tenant code at send time. Example:{' '}
              <code>[linked here](/{'{tenant_code}'}/reports?report=school-referral-needed)</code>
            </p>
          )}
          {saveStatus === 'saving' && <span className="tenant-settings-status">Saving…</span>}
          {saveStatus === 'saved'  && <span className="tenant-settings-status">Saved.</span>}
          {saveStatus === 'error'  && <span className="tenant-settings-status error">Save failed.</span>}
        </div>
      </div>

      <section className="tenant-settings-section">
        <h2 className="nt-section-title">
          Full credential renewal
          <InfoIconButton
            ariaExpanded={recredHintOpen}
            ariaControls="nt-recred-hint"
            onClick={() => setRecredHintOpen((v) => !v)}
            ariaLabel="Show section explanation"
            ariaLabelExpanded="Hide section explanation"
          />
        </h2>
        {recredHintOpen && (
          <p id="nt-recred-hint" className="nt-hint-text">
            Sent when you click <strong>Send notification</strong> on the Training page. Staff see
            their renewal window end date as the subject; this text appears when they expand the
            notification. Leave blank to send no body.
          </p>
        )}
        <div className="nt-section-meta">
          <JobScheduleBadge schedules={schedules} jobCode="credential_renewal_reminder" />
        </div>
        <div className="other-fields">
          <div className="other-row other-row-full">
            <label htmlFor="recredential_notice_details">Notification body</label>
            <CollapsibleBody
              id="recredential_notice_details"
              value={formValues.recredential_notice_details}
              onChange={(e) => handleChange('recredential_notice_details', e.target.value)}
              placeholder="Enter instructions to include in the renewal notification…"
              rows={10}
            />
          </div>
        </div>
      </section>

      <section className="tenant-settings-section">
        <h2 className="nt-section-title">
          End of Month checklist
          <InfoIconButton
            ariaExpanded={eomHintOpen}
            ariaControls="nt-eom-hint"
            onClick={() => setEomHintOpen((v) => !v)}
            ariaLabel="Show section explanation"
            ariaLabelExpanded="Hide section explanation"
          />
        </h2>
        {eomHintOpen && (
          <p id="nt-eom-hint" className="nt-hint-text">
            Sent automatically to all staff with active open referrals at the end of each month.
          </p>
        )}
        <div className="nt-section-meta">
          <JobScheduleBadge schedules={schedules} jobCode="eom_checklist_reminder" />
        </div>
        <div className="other-fields">
          <div className="other-row other-row-full">
            <label htmlFor="eom_checklist_notice_details">Notification body</label>
            <CollapsibleBody
              id="eom_checklist_notice_details"
              value={formValues.eom_checklist_notice_details}
              onChange={(e) => handleChange('eom_checklist_notice_details', e.target.value)}
              placeholder="Enter the end-of-month checklist body…"
              rows={14}
            />
          </div>
        </div>
      </section>

      <section className="tenant-settings-section">
        <h2 className="nt-section-title">
          School consent check
          <InfoIconButton
            ariaExpanded={schoolHintOpen}
            ariaControls="nt-school-hint"
            onClick={() => setSchoolHintOpen((v) => !v)}
            ariaLabel="Show section explanation"
            ariaLabelExpanded="Hide section explanation"
          />
        </h2>
        {schoolHintOpen && (
          <p id="nt-school-hint" className="nt-hint-text">
            Runs on a schedule and checks clients aged 27–35 months who have an open referral and a
            school district assigned. Clients are skipped if their school milestone is already
            completed (referral made) or marked as declined.
            <br /><br />
            For each remaining client, it looks for a consent document in the Intake-Consents
            folder. The file name must contain "to…from" in some form (e.g. "To From", "To and
            From", "To/From") plus a reference to the school district — such as "SCHOOL",
            "DISTRICT", "SD 308", "SD308", or just the district number (e.g. "201" in
            "To and From Minooka 201").
            <br /><br />
            When consent is <strong>missing</strong>, the assigned SC receives one digest
            notification listing all their affected clients with links, repeated each cycle until
            resolved. When consent is <strong>found</strong>, the job silently logs it and stops
            sending missing-consent reminders for that client — no notification is sent. Current
            consent status is visible on the <strong>School Referral Needed</strong> report
            (Consent column).
          </p>
        )}
        <div className="nt-section-meta">
          <JobScheduleBadge schedules={schedules} jobCode="school_consent_check" />
        </div>
        <div className="other-fields">
          <div className="other-row other-row-full">
            <label htmlFor="school_consent_missing_body">
              Notification body — consent missing
              <span className="nt-label-sub"> (one digest per SC per cycle)</span>
              <InfoIconButton
                ariaExpanded={schoolVarsHintOpen}
                ariaControls="nt-school-vars-hint"
                onClick={() => setSchoolVarsHintOpen((v) => !v)}
                ariaLabel="Show available variables"
                ariaLabelExpanded="Hide available variables"
              />
            </label>
            {schoolVarsHintOpen && (
              <p id="nt-school-vars-hint" className="nt-hint-text">
                Available variable: <code>{'{client_lines}'}</code> — replaced with a bulleted list of clients (name, age, district), each linking to their record.
              </p>
            )}
            <CollapsibleBody
              id="school_consent_missing_body"
              value={formValues.school_consent_missing_body}
              onChange={(e) => handleChange('school_consent_missing_body', e.target.value)}
              placeholder={DEFAULT_MISSING_BODY}
              rows={6}
            />
          </div>
        </div>
      </section>

      <section className="tenant-settings-section">
        <h2 className="nt-section-title">
          Delayed service needs
          <InfoIconButton
            ariaExpanded={delayedHintOpen}
            ariaControls="nt-delayed-hint"
            onClick={() => setDelayedHintOpen((v) => !v)}
            ariaLabel="Show section explanation"
            ariaLabelExpanded="Hide section explanation"
          />
        </h2>
        {delayedHintOpen && (
          <p id="nt-delayed-hint" className="nt-hint-text">
            Runs on a schedule and finds service needs still unmatched (no provider assigned) 30+
            days after their Date of Consent. The assigned SC receives one notification per child
            listing that child's delayed needs; it is marked urgent (red) in the inbox. Sent once
            per need. Notify-only — the SC records the delay reason/comment on each need.
          </p>
        )}
        <div className="nt-section-meta">
          <JobScheduleBadge schedules={schedules} jobCode="delayed_need_reminder" />
        </div>
        <div className="other-fields">
          <div className="other-row other-row-full">
            <label htmlFor="delayed_need_reminder_body">
              Notification body
              <span className="nt-label-sub"> (one per child)</span>
              <InfoIconButton
                ariaExpanded={delayedVarsHintOpen}
                ariaControls="nt-delayed-vars-hint"
                onClick={() => setDelayedVarsHintOpen((v) => !v)}
                ariaLabel="Show available variables"
                ariaLabelExpanded="Hide available variables"
              />
            </label>
            {delayedVarsHintOpen && (
              <p id="nt-delayed-vars-hint" className="nt-hint-text">
                Available variables: <code>{'{child_link}'}</code> — child name linking to their
                record; <code>{'{child}'}</code> — child name (plain); <code>{'{count}'}</code> —
                number of delayed needs; <code>{'{threshold_days}'}</code> — the day threshold;
                <code>{'{need_lines}'}</code> — bulleted list of the delayed needs. (If you remove{' '}
                <code>{'{need_lines}'}</code>, the list is still appended.)
              </p>
            )}
            <CollapsibleBody
              id="delayed_need_reminder_body"
              value={formValues.delayed_need_reminder_body}
              onChange={(e) => handleChange('delayed_need_reminder_body', e.target.value)}
              placeholder={DEFAULT_DELAYED_BODY}
              rows={6}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
